import type { WithContext } from "schema-dts";

interface ItemListItemInput {
  id: string;
  title: string;
  description?: string;
  link?: string;
}

interface ItemListElement {
  "@type": "ListItem";
  position: number;
  name: string;
  description?: string;
  url?: string;
}

interface ItemListSchema {
  "@type": "ItemList";
  name?: string;
  description?: string;
  itemListElement: ItemListElement[];
  inLanguage?: string;
}

interface HowToStep {
  "@type": "HowToStep";
  position: number;
  name: string;
  text?: string;
  url?: string;
}

interface HowToSchema {
  "@type": "HowTo";
  name?: string;
  description?: string;
  step: HowToStep[];
  inLanguage?: string;
}

/**
 * Strips HTML tags and converts newlines to spaces for schema
 */
function stripHtmlAndNormalizeText(text: string): string {
  // Remove HTML tags if any
  let cleaned = text.replace(/<[^>]*>/g, "");
  
  // Convert newlines to spaces
  cleaned = cleaned.replace(/\n+/g, " ");
  
  // Normalize multiple spaces to single space
  cleaned = cleaned.replace(/\s+/g, " ");
  
  // Trim whitespace
  return cleaned.trim();
}

/**
 * Extracts the first URL from a text string if present
 */
function extractFirstUrl(text: string): string | undefined {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const match = urlRegex.exec(text);
  return match ? match[1] : undefined;
}

/**
 * Generates ItemList schema from list items
 * 
 * @param items - Array of list items with title, description, and optional link
 * @param title - Optional title for the list
 * @param description - Optional description/intro text for the list
 * @param locale - Language code for inLanguage property
 * @returns ItemList schema object with @context
 */
export function generateItemListSchema(
  items: ItemListItemInput[],
  title?: string,
  description?: string,
  locale: string = "en"
): WithContext<ItemListSchema> {
  const itemListElement: ItemListElement[] = items.map((item, index) => {
    const element: ItemListElement = {
      "@type": "ListItem",
      position: index + 1,
      name: stripHtmlAndNormalizeText(item.title),
    };

    if (item.description) {
      element.description = stripHtmlAndNormalizeText(item.description);
      
      // If description contains URLs and no explicit link is provided, use the first URL
      if (!item.link) {
        const extractedUrl = extractFirstUrl(item.description);
        if (extractedUrl) {
          element.url = extractedUrl;
        }
      }
    }

    // Explicit link takes precedence
    if (item.link) {
      element.url = item.link;
    }

    return element;
  });

  const schema: ItemListSchema = {
    "@type": "ItemList",
    itemListElement,
    inLanguage: locale,
  };

  if (title) {
    schema.name = stripHtmlAndNormalizeText(title);
  }

  if (description) {
    schema.description = stripHtmlAndNormalizeText(description);
  }

  return {
    "@context": "https://schema.org",
    ...schema,
  };
}

/**
 * Generates HowTo schema from list items (for step-by-step guides)
 * 
 * @param items - Array of list items representing steps
 * @param title - Title for the how-to guide
 * @param description - Optional description/intro text
 * @param locale - Language code for inLanguage property
 * @returns HowTo schema object with @context
 */
export function generateHowToSchema(
  items: ItemListItemInput[],
  title?: string,
  description?: string,
  locale: string = "en"
): WithContext<HowToSchema> {
  const steps: HowToStep[] = items.map((item, index) => {
    const step: HowToStep = {
      "@type": "HowToStep",
      position: index + 1,
      name: stripHtmlAndNormalizeText(item.title),
    };

    if (item.description) {
      // For HowTo, use the first line or sentence as the instruction
      const cleanedDescription = stripHtmlAndNormalizeText(item.description);
      const firstSentence = cleanedDescription.split(/[.!?]/)[0];
      step.text = firstSentence ? firstSentence.trim() + "." : cleanedDescription;
    }

    if (item.link) {
      step.url = item.link;
    }

    return step;
  });

  const schema: HowToSchema = {
    "@type": "HowTo",
    step: steps,
    inLanguage: locale,
  };

  if (title) {
    schema.name = stripHtmlAndNormalizeText(title);
  }

  if (description) {
    schema.description = stripHtmlAndNormalizeText(description);
  }

  return {
    "@context": "https://schema.org",
    ...schema,
  };
}

